/**
 * Pure helpers and types for the `ai_dynamic_section` question type (spec-01). No React, no I/O.
 * Config stored FLAT on the question row (not nested) — see ADR-0002 §3.
 */

/** One rubric point the prober covers. Distinct from `QuestionRubric` in `components/survey/types.ts`
 *  (different feature); always carries a stable `id` since coverage keys on it. */
export interface AiDynamicSectionRubricPoint {
  id: string;
  text: string;
  tier: number;
  /** The acceptance bar, read at judge time not just authoring time. Optional. */
  guidance?: string;
}

/** Each field nullable: `null` = "not specified, platform default applies", distinct from a real
 *  `0`. Never coerce an empty input to `0` — see ADR-0002 §3. */
export interface AiDynamicSectionLimits {
  min_questions: number | null;
  max_questions: number | null;
  advisory_seconds: number | null;
  hard_ceiling_seconds: number | null;
}

export interface AiDynamicSectionMediaAsset {
  asset_id: string;
  s3_key: string;
  description?: string | null;
  relevance?: string | null;
}

export interface AiDynamicSectionContextBindings {
  question_ids: string[];
  block_ids: string[];
}

/** The authoring contract, spec-01 "Authoring contract - AiDynamicSectionConfig". */
export interface AiDynamicSectionConfig {
  goal: string;
  topics: string[];
  rubric: AiDynamicSectionRubricPoint[];
  context_bindings: AiDynamicSectionContextBindings;
  media_pool: AiDynamicSectionMediaAsset[];
  limits: AiDynamicSectionLimits;
  allowed_emit_types: string[];
  model: string;
  /** HOW to probe (pacing), distinct from `goal` (WHAT). Read by the prober every turn. */
  probing_instructions: string;
  /** True once the goal/instructions/linked questions changed since `rubric` was derived — surface
   *  it, never regenerate silently. See ADR-0002 §3. */
  rubric_stale?: boolean;
  /** Async rubric derivation status. ABSENT means a pre-existing row — see `effectiveRubricStatus`,
   *  the only place allowed to treat that as anything but pending. See ADR-0002 §3. */
  rubric_status?: RubricStatus;
}

export type RubricStatus = 'pending' | 'ready' | 'failed';

/** The parent survey question row: `id`/`type` plus the (possibly partial) config fields, flat. */
export type AiDynamicSectionQuestion = {
  id: string;
  type: 'ai_dynamic_section';
} & Partial<AiDynamicSectionConfig>;

/** Platform defaults are always concrete numbers, never null — only an author's own choice can be
 *  null. Distinct type so a default-value reader never re-null-checks. */
type RequiredSectionLimits = { [K in keyof AiDynamicSectionLimits]: number };

/** Matches `constants/ai_dynamic_section.py::SectionLimits` defaults exactly. */
export const DEFAULT_SECTION_LIMITS: RequiredSectionLimits = {
  min_questions: 3,
  max_questions: 10,
  advisory_seconds: 180,
  hard_ceiling_seconds: 300,
};

/** Matches `constants/ai_dynamic_section.py::DEFAULT_ALLOWED_EMIT_TYPES`; `max_diff` and
 *  `conjoint_analysis` are banned as too long for one probe turn (spec-01 req. 5). */
export const ALLOWED_EMIT_TYPES = [
  'open_text',
  'single_choice',
  'multiple_choice',
  'rating_scale',
  'matrix_rating',
  'percentage_allocation',
] as const;

export type AllowedEmitType = (typeof ALLOWED_EMIT_TYPES)[number];

/** A freshly-typed section's starting config. `limits`/`allowed_emit_types` mirror the backend's
 *  own defaults; everything else starts empty. */
export const EMPTY_AI_DYNAMIC_SECTION_CONFIG: AiDynamicSectionConfig = {
  goal: '',
  topics: [],
  rubric: [],
  context_bindings: { question_ids: [], block_ids: [] },
  media_pool: [],
  limits: DEFAULT_SECTION_LIMITS,
  allowed_emit_types: [...ALLOWED_EMIT_TYPES],
  model: '',
  probing_instructions: '',
};

/** Reads a builder question row as this section's config. Casts because the row types `rubric` as
 *  the unrelated shape every other question type uses. */
export function dynamicSectionConfigFromRow(row: unknown): AiDynamicSectionConfig {
  const q = (row ?? {}) as Partial<AiDynamicSectionConfig>;
  return {
    goal: q.goal ?? EMPTY_AI_DYNAMIC_SECTION_CONFIG.goal,
    topics: q.topics ?? EMPTY_AI_DYNAMIC_SECTION_CONFIG.topics,
    // Array, not `??`: an ordinary question's probing rubric is a DICT under this same field
    // name and rides along when a type is switched, and every consumer here maps over it.
    rubric: Array.isArray(q.rubric) ? q.rubric : EMPTY_AI_DYNAMIC_SECTION_CONFIG.rubric,
    context_bindings: q.context_bindings ?? EMPTY_AI_DYNAMIC_SECTION_CONFIG.context_bindings,
    media_pool: q.media_pool ?? EMPTY_AI_DYNAMIC_SECTION_CONFIG.media_pool,
    limits: q.limits ?? DEFAULT_SECTION_LIMITS,
    allowed_emit_types: q.allowed_emit_types ?? EMPTY_AI_DYNAMIC_SECTION_CONFIG.allowed_emit_types,
    model: q.model ?? EMPTY_AI_DYNAMIC_SECTION_CONFIG.model,
    probing_instructions:
      q.probing_instructions ?? EMPTY_AI_DYNAMIC_SECTION_CONFIG.probing_instructions,
    rubric_stale: q.rubric_stale,
    rubric_status: q.rubric_status,
  };
}

/** LEGACY fallback: absent `rubric_status` reads as `ready`, never `pending`/`failed`. Every
 *  consumer must go through this. See ADR-0002 §3. */
export function effectiveRubricStatus(rubricStatus: RubricStatus | undefined): RubricStatus {
  return rubricStatus ?? 'ready';
}

/** Prefix for an author-added rubric point id — distinct from the backend derivation service's own
 *  scheme (`rp_<n>`), though only collision-avoidance actually matters here. */
const LOCAL_RUBRIC_POINT_ID_PREFIX = 'rp_local_';

/** An id for an author-added rubric point, guaranteed unique against `existingIds` — coverage rows
 *  key on a point's id, so a collision would silently re-point recorded coverage. */
export function generateRubricPointId(existingIds: readonly string[]): string {
  const used = new Set(existingIds);
  let n = existingIds.length + 1;
  let candidate = `${LOCAL_RUBRIC_POINT_ID_PREFIX}${n}`;
  while (used.has(candidate)) {
    n += 1;
    candidate = `${LOCAL_RUBRIC_POINT_ID_PREFIX}${n}`;
  }
  return candidate;
}

/** The two halves of one backend convention — the upload key and the serve route — which is why
 *  the helpers below convert between them without a second stored field. */
const QUESTION_IMAGE_KEY_PREFIX = 'question-images/';
const QUESTION_IMAGE_SERVE_PREFIX = '/api/question-images/serve/';
const S3_SCHEME = 's3://';

/** `user_id/filename`, the part the key and the serve URL have in common. */
const QUESTION_IMAGE_LOCATOR = /^(\d+)\/([^/?#]+)$/;

/** What the upload endpoint hands back: a displayable URL, and its storage path. */
export interface QuestionImageUploadResult {
  url: string;
  path?: string;
}

/**
 * The `s3_key` to store for an uploaded image, or null — NOT the display URL. The pool reaches the
 * prober as `s3://<s3_key>`, so an external link is refused: it would be sent as `s3://https://…`
 * and attach nothing. `path` is preferred; the URL is the fallback when S3 is off.
 */
export function mediaAssetKeyFromUpload(upload: QuestionImageUploadResult): string | null {
  const path = upload.path?.trim();
  if (path?.startsWith(S3_SCHEME)) {
    const key = path.slice(S3_SCHEME.length);
    if (key) return key;
  }

  const url = upload.url?.trim() ?? '';
  const withoutOrigin = url.split(QUESTION_IMAGE_SERVE_PREFIX);
  if (withoutOrigin.length !== 2) return null;
  const locator = withoutOrigin[1];
  if (!QUESTION_IMAGE_LOCATOR.test(locator)) return null;
  return `${QUESTION_IMAGE_KEY_PREFIX}${locator}`;
}

/** The URL for a stored `s3_key`, or null. The asset carries no display URL (the schema forbids
 *  extra fields), so a reload rebuilds the thumbnail from the key. */
export function questionImageSrcFromKey(s3Key: string): string | null {
  const key = (s3Key ?? '').trim().replace(S3_SCHEME, '');
  if (!key.startsWith(QUESTION_IMAGE_KEY_PREFIX)) return null;
  const locator = key.slice(QUESTION_IMAGE_KEY_PREFIX.length);
  if (!QUESTION_IMAGE_LOCATOR.test(locator)) return null;
  return `${QUESTION_IMAGE_SERVE_PREFIX}${locator}`;
}

const MEDIA_ASSET_ID_PREFIX = 'media_';

/** A globally unique asset id: it is written onto every emitted turn, so a reissued one would
 *  re-point recorded turns at another picture. */
export function generateMediaAssetId(): string {
  // randomUUID needs a secure context; the same fallback shape `respondentId` uses.
  const unique =
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
  return `${MEDIA_ASSET_ID_PREFIX}${unique}`;
}

/** Matches `constants/ai_dynamic_section.py` STOP_* constants. */
export const STOP_COVERED = 'covered';
export const STOP_MAX_QUESTIONS = 'max_questions';
export const STOP_TIMEOUT = 'timeout';
export const STOP_ABANDONED = 'abandoned';
export const STOP_ERROR = 'error';

export type DynamicSectionStopReason =
  | typeof STOP_COVERED
  | typeof STOP_MAX_QUESTIONS
  | typeof STOP_TIMEOUT
  | typeof STOP_ABANDONED
  | typeof STOP_ERROR;

/** One emitted turn - the prober's structured output (spec-01 "Runtime contract"). */
export interface DynamicSectionEmittedQuestion {
  turn: number;
  question_id: string;
  type: string;
  text: string;
  options?: string[] | null;
  type_config?: Record<string, unknown> | null;
  media_asset_id?: string | null;
  targets_rubric_points?: string[] | null;
}

export type DynamicSectionNextResponse =
  | { done: true; stop_reason: DynamicSectionStopReason }
  | { question: DynamicSectionEmittedQuestion };

/** Body of `POST .../answer` — echoes the six emitted-question fields the backend reads back off
 *  `data`; `turn`/`question_id` are ignored server-side, so never sent. */
export interface DynamicSectionAnswerPayload {
  type: string;
  text: string;
  options?: string[] | null;
  type_config?: Record<string, unknown> | null;
  media_asset_id?: string | null;
  targets_rubric_points?: string[] | null;
  answer: unknown;
}

export type DynamicSectionAnswerResponse =
  | { status: 'success'; turns_asked: number }
  | { done: true; stop_reason: DynamicSectionStopReason };

/** The section's `max_questions`, falling back to the backend's own default. */
export function maxQuestionsFor(question: AiDynamicSectionQuestion): number {
  return question.limits?.max_questions ?? DEFAULT_SECTION_LIMITS.max_questions;
}

/** The six emitted-question fields plus the answer — the exact shape `/answer` expects. */
export function buildAnswerPayload(
  question: DynamicSectionEmittedQuestion,
  answer: unknown,
): DynamicSectionAnswerPayload {
  return {
    type: question.type,
    text: question.text,
    options: question.options,
    type_config: question.type_config,
    media_asset_id: question.media_asset_id,
    targets_rubric_points: question.targets_rubric_points,
    answer,
  };
}

const isNonEmptyString = (value: unknown): value is string =>
  typeof value === 'string' && value.trim().length > 0;

/** Whether `answer` completes the emitted `question`, scoped to the six types a section may emit
 *  (spec-01 req. 5) — mirrors `SurveySession.tsx`'s per-type checks, narrowed to this set. */
export function isAnswerComplete(
  question: DynamicSectionEmittedQuestion,
  answer: unknown,
): boolean {
  switch (question.type) {
    case 'open_text':
      return isNonEmptyString(answer);

    case 'single_choice':
      return answer !== null && answer !== undefined && answer !== '';

    case 'multiple_choice':
      return Array.isArray(answer) && answer.length > 0;

    case 'rating_scale':
      return typeof answer === 'number' && Number.isFinite(answer);

    case 'matrix_rating': {
      const rows = (question.type_config?.rows as string[] | undefined) ?? [];
      if (rows.length === 0 || typeof answer !== 'object' || answer === null) return false;
      const given = answer as Record<string, unknown>;
      return rows.every(
        (row) => given[row] !== undefined && given[row] !== null && given[row] !== '',
      );
    }

    case 'percentage_allocation': {
      const options = (question.type_config?.options as string[] | undefined) ?? [];
      const targetSum = (question.type_config?.target_sum as number | undefined) ?? 100;
      if (options.length === 0 || typeof answer !== 'object' || answer === null) return false;
      const given = answer as Record<string, number>;
      const total = options.reduce((sum, option) => sum + (given[option] || 0), 0);
      return total === targetSum;
    }

    default:
      // Unreached: the backend only emits the six allowed types.
      return answer !== null && answer !== undefined && answer !== '';
  }
}

/** What the gate reads off the outstanding-transcript tracker: the runtime counts ATTEMPTS per
 *  question, so it is a Map there and a Set in tests. */
export interface OutstandingTranscripts {
  readonly size: number;
  has(questionId: string): boolean;
}

/** Enough of a survey question row to resolve a `block_ids` binding to the questions it covers. */
export interface BoundQuestionRef {
  id: string;
  block_id?: string | null;
}

/** Still waiting on a bound answer? ADR-0005. Both halves of `context_bindings` count — the
 *  backend expands `block_ids` into the same linked answers. */
export function contextTranscriptsOutstanding(
  config: Pick<AiDynamicSectionConfig, 'context_bindings'>,
  outstanding: OutstandingTranscripts,
  surveyQuestions: readonly BoundQuestionRef[] = [],
): boolean {
  if (outstanding.size === 0) return false;
  const bindings = config.context_bindings;
  if ((bindings?.question_ids || []).some((id) => outstanding.has(String(id)))) return true;

  const boundBlockIds = new Set((bindings?.block_ids || []).map(String));
  if (boundBlockIds.size === 0) return false;
  return surveyQuestions.some(
    (question) =>
      question.block_id != null &&
      boundBlockIds.has(String(question.block_id)) &&
      outstanding.has(String(question.id)),
  );
}
